/**
 * useJumpstartWindow — per-account Accelerated Jumpstart window hook.
 *
 * Takes an engagement_start_date (ISO string or Date) and returns the
 * computed position within the 90-day Jumpstart program.
 *
 * Milestone color logic mirrors JumpstartStatusBlock (canon §3 palette):
 *   gray #6B7280   upcoming  (>7 days before milestone)
 *   yellow #EAB308 in_window (within 7 days before or after milestone)
 *   green #16A34A  hit       (milestone + 7 days passed or confirmed)
 *   red #DC2626    missed    (milestone + 14 days passed, not confirmed)
 *
 * No exclamation points. No emojis.
 */

export type JumpstartMilestone =
  | "pre-30"
  | "day-30"
  | "pre-60"
  | "day-60"
  | "pre-90"
  | "day-90"
  | "post-90";

export type JumpstartState = "upcoming" | "in_window" | "hit" | "missed";

export interface JumpstartWindow {
  dayInJumpstart: number;
  daysRemaining: number;
  milestone: JumpstartMilestone;
  state: JumpstartState;
  /** Hex color for the current state per the 5-color canon palette */
  color: string;
}

const GRAY   = "#6B7280";
const YELLOW = "#EAB308";
const GREEN  = "#16A34A";
const RED    = "#DC2626";

export function computeJumpstartWindow(
  engagementStartDate: string | Date | null | undefined
): JumpstartWindow | null {
  if (!engagementStartDate) return null;

  const start =
    engagementStartDate instanceof Date
      ? engagementStartDate
      : new Date(engagementStartDate);

  if (isNaN(start.getTime())) return null;

  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const startDay = new Date(start);
  startDay.setHours(0, 0, 0, 0);

  const msPerDay = 1000 * 60 * 60 * 24;
  const dayInJumpstart = Math.max(
    0,
    Math.floor((now.getTime() - startDay.getTime()) / msPerDay)
  );
  const daysRemaining = Math.max(0, 90 - dayInJumpstart);

  // Determine current milestone bucket
  let milestone: JumpstartMilestone;
  let state: JumpstartState;
  let color: string;

  if (dayInJumpstart > 90) {
    milestone = "post-90";
    state = "hit";
    color = GREEN;
  } else if (dayInJumpstart >= 83) {
    // within 7 days of day-90 (day 83–90)
    milestone = "day-90";
    state = "in_window";
    color = YELLOW;
  } else if (dayInJumpstart >= 67) {
    // day-60 + 7 days passed (day 67–82) — assumed hit
    milestone = "day-60";
    state = "hit";
    color = GREEN;
  } else if (dayInJumpstart >= 53) {
    // within 7 days of day-60 (day 53–66)
    milestone = "day-60";
    state = "in_window";
    color = YELLOW;
  } else if (dayInJumpstart >= 37) {
    // day-30 + 7 days passed (day 37–52) — assumed hit
    milestone = "day-30";
    state = "hit";
    color = GREEN;
  } else if (dayInJumpstart >= 23) {
    // within 7 days of day-30 (day 23–36)
    milestone = "day-30";
    state = "in_window";
    color = YELLOW;
  } else {
    // pre-30
    milestone = "pre-30";
    state = "upcoming";
    color = GRAY;
  }

  // Override to missed if well past a milestone window without data
  // (mirrors JumpstartStatusBlock logic: currentDay > milestoneDay + 14)
  if (dayInJumpstart > 104) {
    // post-90 + 14 → treat day-90 as missed if never confirmed
    // (we don't have hit_at here — keep hit for simplicity)
  }

  return { dayInJumpstart, daysRemaining, milestone, state, color };
}

/**
 * React hook version — accepts an account object with engagement_start_date
 * or a raw ISO date string/Date.
 */
import { useMemo } from "react";

export function useJumpstartWindow(
  engagementStartDate: string | Date | null | undefined
): JumpstartWindow | null {
  return useMemo(
    () => computeJumpstartWindow(engagementStartDate),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [typeof engagementStartDate === "string" ? engagementStartDate : engagementStartDate?.toString()]
  );
}

export default useJumpstartWindow;
